// lib/analytics/track.ts
// Browser-side analytics beacon for the funnel below "search" — product clicks
// and add-to-cart. Fire-and-forget via navigator.sendBeacon:
//   • sendBeacon SURVIVES page unload — a product click NAVIGATES the host page
//     away (esp. productLinkTarget '_self'), so a normal fetch would be killed
//     mid-flight; the browser guarantees a queued beacon is still sent.
//   • text/plain Blob keeps this a CORS-safelisted ("simple") request → NO
//     preflight; the /api/track route reads request.text() + JSON.parse.
//   • Never throws, never blocks: analytics must not touch the shopper's UX.
//
// The URL MUST carry ?client= — the server resolves the tenant ONLY from that
// query param (resolveClientFromRequest); without it the beacon is attributed to
// the default client (or dropped cross-origin). apiOrigin prefixes the URL so a
// cross-origin embed (e.g. kaizershop.gr) reaches OUR API, not the host site.

export type TrackSource = 'search' | 'agent_chat' | 'browse';

export interface TrackPayload {
  /** Funnel stage. 'purchase' is reserved for a future host order-page snippet. */
  type: 'click' | 'add_to_cart' | 'purchase';
  product_id?: string | number | null;
  /** The search_events id this interaction descends from. Null for browse/agent
   *  contexts that have no joinable search (orphan-by-design). */
  search_id?: string | null;
  visitor_id?: string | null;
  session_id?: string | null;
  /** B2B account id (host's logged-in wholesale customer) — attributes the
   *  interaction to the account. Null for B2C. */
  external_customer_id?: string | null;
  /** Section-local displayed index of the card (not a server rank). */
  position?: number | null;
  source?: TrackSource;
  // add_to_cart extras — server whitelists these into metadata.
  quantity?: number;
  strategy?: string;
  success?: boolean;
  /** Cart/purchase amount; server-clamped. Unused by click/add-to-cart today. */
  value?: number;
}

/**
 * Count one widget MOUNT. Not a search, not an interaction — a separate sink
 * (`/api/track/open` → `widget_open_daily`) that only ever increments two
 * integers, so this can never grow a table again.
 *
 * Replaces a signal we used to get by accident: until 2026-09-09 every mount
 * wrote a NULL-query row into `search_events`, which is how the June-2026
 * syfakb2b.gr embed loop was diagnosed. That was 57% of the analytics table and
 * was removed; this restores the alarm as an aggregate.
 *
 * `first` claims this is the first mount of the browser session — the caller
 * decides, from sessionStorage. `opens` is the loop detector, `sessions` is the
 * human number, and the RATIO between them is what actually reads as healthy or
 * not. Never throws: a blocked or unavailable beacon must not surface.
 */
export function trackWidgetOpen(
  apiOrigin: string | undefined,
  clientId: string,
  first: boolean,
): void {
  try {
    if (typeof navigator === 'undefined' || typeof navigator.sendBeacon !== 'function') return;
    if (!clientId) return;
    const url = `${apiOrigin ?? ''}/api/track/open?client=${encodeURIComponent(clientId)}`;
    // text/plain keeps this CORS-safelisted → no preflight, same as track().
    const blob = new Blob([JSON.stringify({ first })], { type: 'text/plain' });
    navigator.sendBeacon(url, blob);
  } catch {
    // Swallow everything.
  }
}

export function track(
  apiOrigin: string | undefined,
  clientId: string,
  payload: TrackPayload,
): void {
  try {
    if (typeof navigator === 'undefined' || typeof navigator.sendBeacon !== 'function') return;
    if (!clientId) return;
    const url = `${apiOrigin ?? ''}/api/track?client=${encodeURIComponent(clientId)}`;
    const blob = new Blob([JSON.stringify(payload)], { type: 'text/plain' });
    navigator.sendBeacon(url, blob);
  } catch {
    // Swallow everything — a blocked/unavailable beacon must never surface.
  }
}
